
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, FieldArrayPath, useFieldArray, useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { CharacterFormValues } from '@/features/characters';
import { VOICE_ARCHETYPES } from '@common/schemas/entities/character.schema';

type NonNullableVoiceProfile = NonNullable<CharacterFormValues['voiceProfile']>;
type ToneType = NonNullable<NonNullableVoiceProfile['baseStyle']>['tone'];
type DirectnessType = NonNullable<NonNullableVoiceProfile['baseStyle']>['directness'];
type VerbosityType = NonNullable<NonNullableVoiceProfile['baseStyle']>['verbosity'];
type EmotionalExpressionType = NonNullable<NonNullableVoiceProfile['baseStyle']>['emotionalExpression'];

function getTicText(field: Record<string, unknown>): string {
  if (typeof field.value === 'string' && field.value) {
    return field.value;
  }
  const chars = Object.keys(field)
    .filter((k) => k !== 'id' && !isNaN(Number(k)))
    .map((k) => String(field[k]));
  if (chars.length > 0) {
    return chars.join('');
  }
  return '';
}

export function VoiceProfileSection() {
  const { control, setValue, formState: { errors } } = useFormContext<CharacterFormValues>();
  const { t } = useTranslation();
  
  // NOTE: FieldArrayPath assertion is used to bind the voiceProfile.verbalTics path safely.
  const { fields, append, remove } = useFieldArray({
    control,
    name: "voiceProfile.verbalTics" as FieldArrayPath<CharacterFormValues>,
  });
  
  const resolveResponsive = useResponsiveValue();
  const [newTic, setNewTic] = useState("");

  const handleArchetypeChange = (archetypeKey: string) => {
    const archetype = VOICE_ARCHETYPES.find(a => a.key === archetypeKey);
    if (archetype) {
      setValue("voiceProfile.archetype", archetype.name, { shouldDirty: true });
      setValue("voiceProfile.baseStyle.tone", archetype.baseStyle.tone as ToneType, { shouldDirty: true });
      setValue("voiceProfile.baseStyle.directness", archetype.baseStyle.directness as DirectnessType, { shouldDirty: true });
      setValue("voiceProfile.baseStyle.verbosity", archetype.baseStyle.verbosity as VerbosityType, { shouldDirty: true });
      setValue("voiceProfile.baseStyle.emotionalExpression", archetype.baseStyle.emotionalExpression as EmotionalExpressionType, { shouldDirty: true });
    }
  };

  const addTic = () => {
    if (newTic.trim()) {
      append(newTic.trim());
      setNewTic("");
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {t('entity_form.characters.voice_profile.title')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('entity_form.characters.voice_profile.description')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <Controller
            name="voiceProfile.archetype"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Box>
                <FormControl fullWidth variant="standard" error={!!errors.voiceProfile?.archetype}>
                  <InputLabel shrink>{t('entity_form.characters.voice_profile.labels.archetype')}</InputLabel>
                  <Select
                    {...field}
                    value={VOICE_ARCHETYPES.find(a => a.name === field.value)?.key || ''}
                    onChange={(e) => {
                      const key = e.target.value as string;
                      const arch = VOICE_ARCHETYPES.find(a => a.key === key);
                      field.onChange(arch?.name || '');
                      handleArchetypeChange(key);
                    }}
                    sx={{
                      height: 40,
                      fontSize: '0.9rem',
                      '&:before': { borderBottomWidth: 1 },
                      '&:after': { borderBottomWidth: 2 }
                    }}
                  >
                    {VOICE_ARCHETYPES.map(a => (
                      <MenuItem key={a.key} value={a.key}>{t(`entity_form.characters.voice_profile.archetypes.${a.key}`)}</MenuItem>
                    ))}
                  </Select>
                  {errors.voiceProfile?.archetype && <FormHelperText>{errors.voiceProfile.archetype.message}</FormHelperText>}
                </FormControl>
              </Box>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })} />

        <Grid size={resolveResponsive({ xs: 12, md: 3 })}>
          <Controller
            name="voiceProfile.baseStyle.tone"
            control={control}
            defaultValue="formal"
            render={({ field }) => (
              <FormControl fullWidth variant="filled" size="small" error={!!errors.voiceProfile?.baseStyle?.tone}>
                <InputLabel shrink>{t('entity_form.characters.voice_profile.labels.tone')}</InputLabel>
                <Select {...field} disableUnderline sx={{ borderRadius: 1.5 }}>
                  {['formal', 'casual', 'poetic', 'brutal', 'sarcastic', 'humble', 'authoritative', 'childish', 'melancholic'].map(tone => (
                    <MenuItem key={tone} value={tone}>{t(`entity_form.characters.voice_profile.tones.${tone}`)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 3 })}>
          <Controller
            name="voiceProfile.baseStyle.directness"
            control={control}
            defaultValue="direct"
            render={({ field }) => (
              <FormControl fullWidth variant="filled" size="small" error={!!errors.voiceProfile?.baseStyle?.directness}>
                <InputLabel shrink>{t('entity_form.characters.voice_profile.labels.directness')}</InputLabel>
                <Select {...field} disableUnderline sx={{ borderRadius: 1.5 }}>
                  <MenuItem value="direct">{t('entity_form.characters.voice_profile.directness.direct')}</MenuItem>
                  <MenuItem value="indirect">{t('entity_form.characters.voice_profile.directness.indirect')}</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 3 })}>
          <Controller
            name="voiceProfile.baseStyle.verbosity"
            control={control}
            defaultValue="medium"
            render={({ field }) => (
              <FormControl fullWidth variant="filled" size="small" error={!!errors.voiceProfile?.baseStyle?.verbosity}>
                <InputLabel shrink>{t('entity_form.characters.voice_profile.labels.verbosity')}</InputLabel>
                <Select {...field} disableUnderline sx={{ borderRadius: 1.5 }}>
                  <MenuItem value="low">{t('entity_form.characters.voice_profile.verbosity.low')}</MenuItem>
                  <MenuItem value="medium">{t('entity_form.characters.voice_profile.verbosity.medium')}</MenuItem>
                  <MenuItem value="high">{t('entity_form.characters.voice_profile.verbosity.high')}</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 3 })}>
          <Controller
            name="voiceProfile.baseStyle.emotionalExpression"
            control={control}
            defaultValue="balanced"
            render={({ field }) => (
              <FormControl fullWidth variant="filled" size="small" error={!!errors.voiceProfile?.baseStyle?.emotionalExpression}>
                <InputLabel shrink>{t('entity_form.characters.voice_profile.labels.emotional_expression')}</InputLabel>
                <Select {...field} disableUnderline sx={{ borderRadius: 1.5 }}>
                  <MenuItem value="contained">{t('entity_form.characters.voice_profile.emotional_expression.contained')}</MenuItem>
                  <MenuItem value="balanced">{t('entity_form.characters.voice_profile.emotional_expression.balanced')}</MenuItem>
                  <MenuItem value="expressive">{t('entity_form.characters.voice_profile.emotional_expression.expressive')}</MenuItem>
                  <MenuItem value="volatile">{t('entity_form.characters.voice_profile.emotional_expression.volatile')}</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
            {t('entity_form.characters.voice_profile.labels.verbal_tics')}
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {fields.map((field: Record<string, unknown>, index: number) => (
              <Box key={String(field.id || index)} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>{getTicText(field)}</Typography>
                <IconButton size="small" onClick={() => remove(index)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
          <TextField
            value={newTic}
            onChange={(e) => setNewTic(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTic();
              }
            }}
            placeholder={t('entity_form.characters.voice_profile.labels.add_tic_placeholder')}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button onClick={addTic} startIcon={<AddIcon />} size="small">{t('entity_form.characters.voice_profile.labels.add_button')}</Button>
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
